<template>
  <div class="depreciation-container depreciation-page--glass glass-tab-page">
    <BrucdcumHeader
      :breadcrumb="['Fixed Assets', 'Depreciation']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush depreciation-glass-shell">
      <div class="glass-tab-view__content depreciation-page-stack">
        <div class="depreciation-header-block">
          <div class="depreciation-header-row">
            <div class="depreciation-header-row__text">
              <h2 class="depreciation-title">Depreciation</h2>
              <p class="depreciation-subtitle">
                Run depreciation for fixed assets within a date range
              </p>
            </div>
            <div class="depreciation-header-actions">
              <SecondaryButton
                label="Start Date"
                icon="bi bi-calendar-event"
                @click="showStartDateModal = true"
              />
            </div>
          </div>
        </div>

        <div class="depreciation-filter-panel glass-tab-panel--nested">
          <div class="glass-tab-panel__row">
            <div class="depreciation-filter-field">
              <LabelDateField
                input-id="depreciation-from-date"
                label="Depreciated From"
                v-model="fromDate"
                flex
              />
            </div>
            <div class="depreciation-filter-field">
              <LabelDateField
                input-id="depreciation-to-date"
                label="Depreciate To"
                v-model="toDate"
                flex
              />
            </div>
          </div>
        </div>

        <div class="depreciation-table-panel glass-tab-panel--nested">
          <h3 class="depreciation-table-heading">Accounts</h3>
          <div class="depreciation-table-section consultant-glass-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table depreciation-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Amount to be depreciated</th>
                      <th>Accumulated depreciation Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="depreciationRows.length === 0">
                      <td colspan="3" class="consultant-glass-table-no-records depreciation-no-records">
                        No depreciation records to show
                      </td>
                    </tr>
                    <tr
                      v-for="(row, idx) in depreciationRows"
                      :key="row.id || row.account || idx"
                      class="consultant-glass-table-row depreciation-table-row"
                      :style="{ '--dep-row-delay': `${0.04 + Math.min(idx, 14) * 0.025}s` }"
                    >
                      <td>{{ row.account }}</td>
                      <td class="depreciation-amount-cell">{{ formatCurrency(row.amount) }}</td>
                      <td>{{ row.accumulatedDepreciationAccount }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="depreciation-actions-panel glass-tab-panel--nested">
          <div class="depreciation-action-buttons">
            <PrimaryButton label="Update" type="button" />
            <SecondaryButton label="Cancel" type="button" @click="handleCancel" />
          </div>
        </div>
      </div>
    </div>

    <Transition name="depreciation-modal-overlay">
      <div
        v-if="showStartDateModal"
        class="depreciation-modal-overlay depreciation-modal-overlay--glass"
        @click.self="closeStartDateModal"
      >
        <Transition name="depreciation-modal-panel" appear>
          <div
            v-if="showStartDateModal"
            class="depreciation-modal-card depreciation-modal-card--glass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="depreciation-start-date-title"
            @click.stop
          >
            <div class="depreciation-modal-header depreciation-modal-header--glass">
              <h2 id="depreciation-start-date-title" class="depreciation-modal-title">Start Date</h2>
              <button
                type="button"
                class="depreciation-modal-close-btn"
                aria-label="Close"
                title="Close"
                @click="closeStartDateModal"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>

            <div class="depreciation-modal-body depreciation-modal-body--glass">
              <p class="depreciation-modal-info">
                Current start date is: {{ startDate }}
              </p>
              <p class="depreciation-modal-desc">
                The start date for managing your fixed assets will be 1st of the first month in your financial year.
              </p>
              <LabelDateField
                input-id="depreciation-new-start-date"
                label="New Start Date"
                v-model="newStartDate"
                flex
              />
            </div>

            <div class="depreciation-modal-actions depreciation-modal-actions--glass">
              <SecondaryButton label="Cancel" @click="closeStartDateModal" />
              <PrimaryButton label="OK" @click="okStartDate" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script>
import '../styles/Depreciation.css';
import '../styles/depreciation-glass.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'Depreciation',
  components: {
    BrucdcumHeader,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      fromDate: '',
      toDate: '',
      showStartDateModal: false,
      startDate: '04/01/2021',
      newStartDate: '04/01/2021',
      depreciationRows: [],
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId;
    },
    backFallbackRoute() {
      const companyId = this.companyId || this.$route.params.id;
      return `/company/${companyId}/dashboard/company/fixed-assets/pending-items`;
    },
  },
  methods: {
    formatCurrency(value) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(numericValue);
    },
    closeStartDateModal() {
      this.newStartDate = this.startDate;
      this.showStartDateModal = false;
    },
    okStartDate() {
      this.startDate = this.newStartDate;
      this.showStartDateModal = false;
    },
    handleCancel() {
      this.$router.go(-1);
    },
  },
};
</script>
